"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon, Clock, X } from "lucide-react"
import type { Matcher } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface DateTimePickerProps {
  value?: Date | string
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  className?: string
  /** Compact mode for inline use in tables/rows */
  compact?: boolean
}

function parseValue(value: Date | string | undefined): Date | undefined {
  if (!value) return undefined
  if (value instanceof Date) return value
  const d = new Date(value)
  return isNaN(d.getTime()) ? undefined : d
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick date & time",
  disabled = false,
  minDate,
  maxDate,
  className,
  compact = false,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const date = parseValue(value)

  const hours = date ? date.getHours() : 12
  const minutes = date ? date.getMinutes() : 0

  const disabledDates: Matcher[] = []
  if (minDate) disabledDates.push({ before: minDate })
  if (maxDate) disabledDates.push({ after: maxDate })

  const handleDateSelect = (newDate: Date | undefined) => {
    if (!newDate) return
    const result = new Date(newDate)
    result.setHours(hours)
    result.setMinutes(minutes)
    result.setSeconds(0)
    result.setMilliseconds(0)
    onChange?.(result)
  }

  const handleHourChange = (hourStr: string) => {
    const d = date ? new Date(date) : new Date()
    d.setHours(parseInt(hourStr, 10))
    d.setMinutes(minutes)
    d.setSeconds(0)
    d.setMilliseconds(0)
    onChange?.(d)
  }

  const handleMinuteChange = (minStr: string) => {
    const d = date ? new Date(date) : new Date()
    d.setHours(hours)
    d.setMinutes(parseInt(minStr, 10))
    d.setSeconds(0)
    d.setMilliseconds(0)
    onChange?.(d)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange?.(undefined)
  }

  const buttonHeight = compact ? "h-8 text-xs" : "h-10 text-sm"

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal",
            buttonHeight,
            !date && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
          {date ? (
            <span className="truncate">
              {format(date, compact ? "MMM d, HH:mm" : "MMM d, yyyy 'at' HH:mm")}
            </span>
          ) : (
            <span className="truncate">{placeholder}</span>
          )}
          {date && !disabled && (
            <X
              className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50 hover:opacity-100"
              onClick={handleClear}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect}
          disabled={disabledDates.length > 0 ? disabledDates : undefined}
          autoFocus
        />
        <div className="border-t p-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Time:</span>
            <Select value={String(hours)} onValueChange={handleHourChange}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {String(i).padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm font-medium">:</span>
            <Select value={String(minutes)} onValueChange={handleMinuteChange}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {String(m).padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
